import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import '@fontsource/material-icons';

import { createTheme } from '@mui/material/styles';

const borderRadius = 10;

const buildTheme = palette =>
  createTheme({
    palette,
    shape: {
      borderRadius,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: [
          palette.mode === 'dark'
            ? `
        /* :not(.MuiButtonBase-root) keeps this off Buttons/IconButtons that
           render as anchors because they were given an href. a:visited and
           a:hover outrank MUI's own class, so a visited <Button href> used to
           lose its contrastText and paint the link colour on top of its own
           background. */
        a:not(.MuiButtonBase-root),
        a:not(.MuiButtonBase-root):visited,
        a:not(.MuiButtonBase-root):hover {
          color: ${palette.link};
        }
        /* Labels are static and the legend is hidden, so MUI's -5px notch
           offset just leaves an unfilled band above the input. Invisible on
           light, visible against dark surfaces. */
        .MuiOutlinedInput-root fieldset {
          top: 0;
        }
        /* The blanket .MuiInputBase-root background below paints the standard
           variant selects too, which reads as a stray box in table toolbars. */
        .MuiTablePagination-root .MuiInputBase-root {
          background: transparent;
        }
      `
            : '',
          `
        body {
          background: ${palette.background.content};
        }
        input::-webkit-outer-spin-button,
        input::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0; 
        }
        input[type=number] {
            -moz-appearance:textfield;
        }

        pre {
          white-space: pre-wrap;       /* Since CSS 2.1 */
          white-space: -moz-pre-wrap;  /* Mozilla, since 1999 */
          white-space: -pre-wrap;      /* Opera 4-6 */
          white-space: -o-pre-wrap;    /* Opera 7 */
          word-wrap: break-word;       /* Internet Explorer 5.5+ */
      }

        .MuiInputBase-root {
          background: ${palette.inputBackground};
        }
        .MuiFormControl-root .MuiFormLabel-root{
          transform: none;
          position: static;
          margin-bottom: 8px;
        }
        .MuiFormLabel-root{
          font-weight: 500 !important;
          font-size: 0.87rem !important;
        }
        .MuiFormControl-root legend span{
          display: none;
        }
        .MuiFormControl-root .MuiFormHelperText-root.Mui-error{
          font-size: 0.85rem;
        }
        .Mui-datepicker .MuiFormControl-root fieldset{
          top:0 !important;
        }
        .MuiInputBase-root.MuiInputBase-adornedStart{
          padding-left: 6px;
        }
        .MuiInputAdornment-root.MuiInputAdornment-positionStart{
          margin-right: 3px;
        }
        .MuiButton-startIcon{
          margin-right: 2px !important;
        }
        .MuiButton-startIcon svg{
          width:0.8em;
        }
        .MuiButtonBase-root.MuiTab-root{
          text-transform: none;
        }
        .MuiOutlinedInput-root.Mui-disabled{
          opacity:0.5;
        }
        .mapboxgl-ctrl{
          pointer-events: auto;
          margin: 5px;
        }
        
        /* Target the Mapbox Draw control container */
        .mapboxgl-ctrl.mapboxgl-ctrl-group {
          padding: 2px;
          background-color: ${palette.background.paper};
          border-radius: 8px;
          box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.2);
        }

        /* Style the buttons */
        .mapbox-gl-draw_ctrl-draw-btn {
          width: 35px;
          height: 35px;
          border-radius: 8px;
          border: none;
          background-color: ${palette.background.paper};
          color: ${palette.mapControlIcon};
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.2s ease;
        }

        .mapbox-gl-draw_ctrl-draw-btn:hover {
          background-color: ${palette.primaryContainer}; /* light blue hover */
        }

        /* Optional: icon scaling */
        .mapbox-gl-draw_ctrl-draw-btn svg {
          width: 20px;
          height: 20px;
        }
        /* The block above was missing its closing brace, so everything below it
           was swallowed by the parser and has never applied. The brace is now
           closed; these three rules stay commented out because enabling them
           would shift light-mode table and pagination layout, which belongs in
           its own visual-change PR.

        .MuiBox-root.Mui-TableHeadCell-Content-Labels{
          align-items: flex-start;
        }
        .MuiBox-root.Mui-TableHeadCell-Content-Labels > .MuiBadge-root{
          margin-top: 3px;
        }
        .MuiTablePagination-root.MuiBox-root{
          padding: 10px 0 0 0;
        }
        */
      `,
        ].join(''),
      },
      MuiPaper: {
        styleOverrides: {
          // MUI tints dark-mode Paper by elevation, which washed dialogs and
          // menus to a light grey instead of the surface token.
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: ({ theme }) => ({
            borderColor: theme.palette.inputBorder,
          }),
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiInputBase-sizeSmall': {
              height: '34px', // or adjust minHeight if needed
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          inputSizeSmall: {
            padding: '0px 8px', // tighter padding
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            textTransform: 'none',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            '&.MuiTableCell-head.MuiTableCell-sizeMedium': {
              padding: '3px',
            },
            '&.MuiTableCell-sizeMedium': {
              padding: '10px',
            },
            '&.MuiTableCell-root.MuiTableCell-alignLeft': {
              paddingLeft: '3px',
            },
          },
        },
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            '&.MuiAccordionDetails-root': {
              marginTop: '0px',
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: ({ theme }) => ({
            fontSize: '0.875rem',
            color: theme.palette.helperText,
            '&.Mui-disabled': {
              opacity: 1,
              color: theme.palette.helperText,
            },
            '&.Mui-error': {
              color: theme.palette.error.main,
            },
          }),
        },
      },
    },
  });

const lightPalette = {
  mode: 'light',
  primary: {
    dark: '#0076a7',
    main: '#009ACD',
    light: '#CCEBF5',
    contrastText: '#ffffff',
  },
  secondary: {
    dark: '#7e7f80',
    main: '#D7D7D7',
    light: '#EAEBEC',
    contrastText: '#000000',
  },
  tertiary: {
    main: '#000000',
    contrastText: '#ffffff',
  },
  success: {
    main: '#2E7D32',
    light: '#4CAF50',
    dark: '#1B5E20',
  },
  background: {
    default: '#ffffff',
    paper: '#ffffff',
    content: '#f8f8f8',
  },
  helperText: '#424443',
  inputBackground: '#ffffff',
  inputBorder: 'rgba(0, 0, 0, 0.23)',
  warningContainer: '#fffbf0',
  errorContainer: '#fff0f0',
  primaryContainer: '#e0e7ff',
  mapControlIcon: '#2563eb',
  neutral: {
    subtle: '#fafafa',
    muted: '#f5f5f5',
    default: '#eeeeee',
    strong: '#e0e0e0',
  },
  link: null,
  mutedText: '#7e7f80',
  mutedBorder: '#7e7f80',
  stepperTrack: '#F2F2F2',
  stepperTrackActive: '#CCEBF5',
};

const darkPalette = {
  mode: 'dark',
  primary: {
    main: '#3DBBE0',
    dark: '#009ACD',
    light: '#8ADCF2',
    contrastText: '#06232C',
  },
  secondary: {
    main: '#3A454C',
    light: '#4B575F',
    dark: '#2C363D',
    contrastText: '#E7ECEE',
  },
  tertiary: {
    main: '#E7ECEE',
    contrastText: '#12181C',
  },
  success: {
    main: '#5FBF63',
    light: '#8AD48E',
    dark: '#37864B',
  },
  error: {
    main: '#F2736A',
  },
  warning: {
    main: '#FFB84D',
  },
  info: {
    main: '#6AB7F5',
  },
  background: {
    default: '#12181C',
    paper: '#171F24',
    content: '#0E1417',
  },
  divider: '#2A343B',
  text: {
    primary: '#E7ECEE',
    secondary: '#A2AFB6',
    disabled: '#5F6C74',
  },
  helperText: '#A2AFB6',
  inputBackground: '#0F151A',
  inputBorder: '#3B4750',
  warningContainer: '#3D2A14',
  errorContainer: '#3A1F1F',
  primaryContainer: '#0E3340',
  mapControlIcon: '#8ADCF2',
  neutral: {
    subtle: '#1A2126',
    muted: '#1E272D',
    default: '#242E35',
    strong: '#2F3A42',
  },
  link: '#8ADCF2',
  mutedText: '#A2AFB6',
  mutedBorder: '#3B4750',
  stepperTrack: '#242E35',
  stepperTrackActive: '#0E3340',
};

export const lightTheme = buildTheme(lightPalette);
export const darkTheme = buildTheme(darkPalette);
